import { useEffect, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';

import { FairProvider } from './api/FairProvider';
import { AuthProvider } from './auth/AuthProvider';
import { AppHeader } from './components/layout/AppHeader';
import { FairMessageBar } from './components/layout/FairMessageBar';
import { showCookiePreferences } from './cookie-consent';
import { ArticleDetailsPage } from './pages/ArticleDetailsPage';
import { ArticlesPage } from './pages/ArticlesPage';
import { HomePage } from './pages/HomePage';
import { JobDetailsPage } from './pages/JobDetailsPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PavilionPage } from './pages/PavilionPage';
import { SearchPage } from './pages/SearchPage';
import { StandPage } from './pages/StandPage';
import { TreasureHuntProvider } from './treasure-hunt/TreasureHuntProvider';

/** A client-side route change keeps the previous scroll offset, which lands the
 *  reader mid-page on the next screen. */
function ScrollToTop(): null {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

/** The only place to revisit cookie consent once the banner is dismissed; the
 *  app has no menu to hold it. */
function SiteFooter(): ReactElement {
  const { t } = useTranslation();

  return (
    <footer className="app-shell pt-4 pb-8 text-center">
      <button
        type="button"
        onClick={showCookiePreferences}
        className="text-sm text-fog-500 underline-offset-4 hover:underline"
      >
        {t('common.cookieSettings')}
      </button>
    </footer>
  );
}

export function App(): ReactElement {
  return (
    <BrowserRouter>
      <ScrollToTop />
      {/* Above <Routes>: the header renders on every route and needs the fair,
          so no page-level fetch can supply it. */}
      <FairProvider>
        <AuthProvider>
          <TreasureHuntProvider>
            <FairMessageBar />
            <AppHeader />
            <main>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/categories/:pavilionSlug" element={<PavilionPage />} />
                <Route path="/exhibitors/:standSlug" element={<StandPage />} />
                <Route path="/exhibitors/:standSlug/jobs/:jobId" element={<JobDetailsPage />} />
                {/* Short job links, e.g. from outside the app: the page looks the job
                    up and replaces the URL with its exhibitor's. */}
                <Route path="/jobs/:jobId" element={<JobDetailsPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/news" element={<ArticlesPage />} />
                <Route path="/news/:articleId" element={<ArticleDetailsPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </main>
            <SiteFooter />
          </TreasureHuntProvider>
        </AuthProvider>
      </FairProvider>
    </BrowserRouter>
  );
}
